import { IconCalendarFilled } from '@iwoca/icons';
import { Button } from '@iwoca/orion';
import { FormattedMessage } from 'react-intl';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';

import { BlendedRateSummary } from './BlendedRateSummary/BlendedRateSummary';
import LoadingScheduleValue from './LoadingScheduleValue';
import styles from './RepaymentScheduleSummary.module.css';
import { useGetRepaymentScheduleSummaryData } from './useGetRepaymentScheduleSummaryData';
import { SHOW_REPAYMENT_SCHEDULE_MODAL } from '../../../modals/repaymentScheduleModal/RepaymentScheduleModal.actions';
import { MoneyWithDecimals } from '../../../ui/moneyWithDecimals/MoneyWithDecimals';

const mapDispatchToProps = (dispatch: Dispatch) => ({
    showSchedule: (accountId: string) =>
        dispatch({
            type: SHOW_REPAYMENT_SCHEDULE_MODAL,
            accountId: accountId
        })
});

type PropsFromRedux = ReturnType<typeof mapDispatchToProps>;

function RepaymentScheduleSummary({
    accountId,
    showSchedule,
    topupDrawdownAmount
}: { accountId: string; topupDrawdownAmount: number } & PropsFromRedux) {
    const {
        durationInMonths,
        isInterestOnlyLoan,
        finalMonthsRepaymentAmount,
        equalMonthlyAmount,
        totalInterest,
        totalBalance,
        fees,
        blendedRateDetails,
        interestOnlyPeriod
    } = useGetRepaymentScheduleSummaryData(accountId, topupDrawdownAmount);

    return (
        <div className={styles.repaymentScheduleContainer}>
            <BlendedRateSummary
                topupDrawdownAmount={topupDrawdownAmount}
                blendedRateDetails={blendedRateDetails}
            />
            <div className={styles.repaymentScheduleTitleContainer}>
                <span className={styles.repaymentScheduleTitle}>
                    <FormattedMessage
                        id="_.drawdown_action_panel.topup_drawdown.repayment_schedule_summary.repaymentsTitle"
                        defaultMessage="Your repayments over {monthsSchedule} months"
                        values={{ monthsSchedule: durationInMonths }}
                    />
                </span>

                <Button
                    variant="tertiary"
                    onClick={() => showSchedule(accountId)}
                >
                    <FormattedMessage
                        id="_.drawdown_action_panel.topup_drawdown.repayment_schedule_summary.repaymentsScheduleCTA"
                        defaultMessage="See your new schedule"
                    />
                    <IconCalendarFilled />
                </Button>
            </div>
            {isInterestOnlyLoan ? (
                <div className={styles.repaymentScheduleSummaryValueContainer}>
                    <div className={styles.interestOnlyMessage}>
                        <FormattedMessage
                            tagName="span"
                            id="_.drawdown_action_panel.topup_drawdown.repayment_schedule_summary.minimumRepaymentsLabelInterestOnly"
                            defaultMessage="Minimum monthly payment (from the {interestFreeMonthNumber}th month)"
                            values={{
                                interestFreeMonthNumber: interestOnlyPeriod
                                    ? interestOnlyPeriod + 1
                                    : null
                            }}
                        />
                        <FormattedMessage
                            tagName="span"
                            id="_.drawdown_action_panel.topup_drawdown.repayment_schedule_summary.minimumRepaymentsSubLabelInterestOnly"
                            defaultMessage="(Interest payments due from the 1st month)"
                        />
                    </div>

                    <LoadingScheduleValue>
                        <MoneyWithDecimals
                            value={finalMonthsRepaymentAmount}
                            className={styles.repaymentScheduleSummaryValue}
                        />
                    </LoadingScheduleValue>
                </div>
            ) : (
                <div className={styles.repaymentScheduleSummaryValueContainer}>
                    <FormattedMessage
                        id="_.drawdown_action_panel.topup_drawdown.repayment_schedule_summary.minimumRepaymentsLabel"
                        defaultMessage="Minimum monthly repayment"
                    />

                    <LoadingScheduleValue>
                        <MoneyWithDecimals
                            value={equalMonthlyAmount}
                            className={styles.repaymentScheduleSummaryValue}
                        />
                    </LoadingScheduleValue>
                </div>
            )}
            <div className={styles.repaymentScheduleSummaryValueContainer}>
                <FormattedMessage
                    id="_.drawdown_action_panel.topup_drawdown.repayment_schedule_summary.totalInterestLabel"
                    defaultMessage="Total interest"
                />

                <LoadingScheduleValue>
                    <MoneyWithDecimals
                        value={totalInterest}
                        className={styles.repaymentScheduleSummaryValue}
                    />
                </LoadingScheduleValue>
            </div>
            <div className={styles.repaymentScheduleSummaryValueContainer}>
                <FormattedMessage
                    id="_.drawdown_action_panel.topup_drawdown.repayment_schedule_summary.feesLabel"
                    defaultMessage="Fees"
                />{' '}
                <LoadingScheduleValue>
                    <MoneyWithDecimals
                        value={fees}
                        className={styles.repaymentScheduleSummaryValue}
                    />
                </LoadingScheduleValue>
            </div>
            <div className={styles.repaymentScheduleSummaryValueContainer}>
                <FormattedMessage
                    id="_.drawdown_action_panel.topup_drawdown.repayment_schedule_summary.totalRepaymentLabel"
                    defaultMessage="Total repayment"
                />

                <LoadingScheduleValue>
                    <MoneyWithDecimals
                        value={totalBalance}
                        className={styles.repaymentScheduleSummaryValue}
                    />
                </LoadingScheduleValue>
            </div>
        </div>
    );
}

// eslint-disable-next-line import/no-default-export
export default connect(null, mapDispatchToProps)(RepaymentScheduleSummary);
